import { useEffect, useMemo, useState } from "react";

import {
  buildMonthCells,
  EVENT_CALENDAR_MONTH_TITLE_FORMATTER,
  EVENT_CALENDAR_WEEKDAY_LABELS,
  formatMonthKey,
  monthStartFromKey,
  preferredMonthKey,
  shiftMonth,
} from "./eventPlannerCalendar";
import { hasEventRecurrence, listUpcomingOccurrences, materializeEventOccurrences } from "./eventPlannerRecurrence";
import { formatEventLocation, type EventRecord } from "./eventPlannerUtils";
import { Icon } from "./eventPlannerIcons";

const RECURRING_EVENTS_V1_ENABLED = false;

type EventCalendarViewProps = {
  events: EventRecord[];
  todayIso: string;
  onOpenEvent: (eventId: number, occurrenceDate?: string, sourceDate?: string) => void;
};

export function EventCalendarView({ events, todayIso, onOpenEvent }: EventCalendarViewProps) {
  const materializeCalendarEntries = (event: EventRecord, rangeStart: string, rangeEnd: string) => {
    if (RECURRING_EVENTS_V1_ENABLED) {
      return materializeEventOccurrences(event, { rangeStart, rangeEnd });
    }
    if (!event.date.trim() || event.date < rangeStart || event.date > rangeEnd) {
      return [];
    }
    return [
      {
        event,
        key: `${event.id}:${event.date}:${event.start_time}:${event.end_time}:${event.timezone}`,
        source_date: event.date,
        date: event.date,
        start_time: event.start_time,
        end_time: event.end_time,
        timezone: event.timezone,
        is_override: false,
      },
    ];
  };
  const calendarAnchors = useMemo(
    () =>
      events
        .flatMap((event) => {
          if (!event.date.trim()) {
            return [];
          }
          if (RECURRING_EVENTS_V1_ENABLED) {
            const firstUpcomingOccurrence = listUpcomingOccurrences(event, todayIso, 1)[0];
            return [{ date: firstUpcomingOccurrence?.date || event.date }];
          }
          return [{ date: event.date }];
        })
        .sort((left, right) => left.date.localeCompare(right.date)),
    [events, todayIso]
  );
  const [visibleMonth, setVisibleMonth] = useState(() => preferredMonthKey(calendarAnchors, todayIso));

  useEffect(() => {
    if (calendarAnchors.length === 0) {
      setVisibleMonth(formatMonthKey(new Date()));
      return;
    }

    const monthCells = buildMonthCells(visibleMonth);
    const rangeStart = monthCells[0]?.iso || `${visibleMonth}-01`;
    const rangeEnd = monthCells[monthCells.length - 1]?.iso || rangeStart;
    const hasEventInVisibleMonth = events.some((event) => materializeCalendarEntries(event, rangeStart, rangeEnd).length > 0);
    if (!hasEventInVisibleMonth) {
      setVisibleMonth(preferredMonthKey(calendarAnchors, todayIso));
    }
  }, [calendarAnchors, events, todayIso, visibleMonth]);

  const monthCells = useMemo(() => buildMonthCells(visibleMonth), [visibleMonth]);
  const visibleRangeStart = monthCells[0]?.iso || `${visibleMonth}-01`;
  const visibleRangeEnd = monthCells[monthCells.length - 1]?.iso || visibleRangeStart;

  const occurrencesByDate = useMemo(() => {
    const grouped = new Map<string, ReturnType<typeof materializeEventOccurrences>>();
    events.forEach((event) => {
      materializeCalendarEntries(event, visibleRangeStart, visibleRangeEnd).forEach((occurrence) => {
        grouped.set(occurrence.date, [...(grouped.get(occurrence.date) || []), occurrence]);
      });
    });
    return grouped;
  }, [events, visibleRangeEnd, visibleRangeStart]);

  const visibleMonthStart = useMemo(() => monthStartFromKey(visibleMonth), [visibleMonth]);
  const visibleMonthEventsCount = useMemo(
    () =>
      Array.from(occurrencesByDate.values())
        .flat()
        .filter((occurrence) => occurrence.date.startsWith(visibleMonth)).length,
    [occurrencesByDate, visibleMonth]
  );

  return (
    <section className="event-calendar-panel">
      <div className="event-calendar-toolbar">
        <div className="event-calendar-toolbar-main">
          <h3 className="event-calendar-title">{EVENT_CALENDAR_MONTH_TITLE_FORMATTER.format(visibleMonthStart)}</h3>
          <p className="muted event-calendar-copy">
            {visibleMonthEventsCount > 0
              ? `${visibleMonthEventsCount} évènement${visibleMonthEventsCount > 1 ? "s" : ""} dans ce mois.`
              : "Aucun évènement visible sur ce mois avec les filtres actuels."}
          </p>
        </div>

        <div className="event-calendar-actions">
          <button
            type="button"
            className="event-calendar-nav-button"
            aria-label="Mois précédent"
            title="Mois précédent"
            onClick={() => setVisibleMonth(shiftMonth(visibleMonth, -1))}
          >
            <Icon name="arrow-left" className="ui-icon event-calendar-nav-icon" />
          </button>
          <button
            type="button"
            className="event-calendar-today-button"
            onClick={() => setVisibleMonth(formatMonthKey(new Date()))}
          >
            Aujourd'hui
          </button>
          <button
            type="button"
            className="event-calendar-nav-button"
            aria-label="Mois suivant"
            title="Mois suivant"
            onClick={() => setVisibleMonth(shiftMonth(visibleMonth, 1))}
          >
            <Icon name="arrow-left" className="ui-icon event-calendar-nav-icon is-next" />
          </button>
        </div>
      </div>

      {calendarAnchors.length === 0 ? (
        <div className="event-calendar-empty">
          <strong>Aucun évènement daté à afficher.</strong>
          <p className="muted">Ajoutez une date à un brouillon ou ajustez les filtres pour alimenter le calendrier.</p>
        </div>
      ) : (
        <div className="event-calendar-scroll">
          <div className="event-calendar-grid" role="grid" aria-label="Calendrier des évènements">
            {EVENT_CALENDAR_WEEKDAY_LABELS.map((label) => (
              <div key={label} className="event-calendar-weekday" role="columnheader">
                {label}
              </div>
            ))}

            {monthCells.map((cell) => {
              const cellEvents = occurrencesByDate.get(cell.iso) || [];
              const isToday = cell.iso === todayIso;

              return (
                <div
                  key={cell.iso}
                  className={[
                    "event-calendar-cell",
                    cell.isCurrentMonth ? "is-current-month" : "is-outside-month",
                    isToday ? "is-today" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  role="gridcell"
                >
                  <div className="event-calendar-cell-head">
                    <span className="event-calendar-day-number">{cell.date.getDate()}</span>
                  </div>

                  <div className="event-calendar-day-events">
                    {cellEvents.slice(0, 2).map((occurrence) => {
                      const locationLabel = formatEventLocation(occurrence.event);
                      const openAsOccurrence =
                        RECURRING_EVENTS_V1_ENABLED && hasEventRecurrence(occurrence.event.recurrence);

                      return (
                        <button
                          key={occurrence.key}
                          type="button"
                          className={`event-calendar-event${occurrence.event.publication_status === "draft" ? " is-draft" : ""}`}
                          title={occurrence.event.display_name}
                          aria-label={occurrence.event.display_name}
                          onClick={() =>
                            onOpenEvent(
                              occurrence.event.id,
                              openAsOccurrence ? occurrence.date : undefined,
                              openAsOccurrence ? occurrence.source_date : undefined
                            )
                          }
                        >
                          <span className="event-calendar-event-body">
                            <strong className="event-calendar-event-name">{occurrence.event.display_name}</strong>
                            <span className="event-calendar-event-time">{occurrence.start_time || "--:--"}</span>
                            {locationLabel ? <span className="event-calendar-event-location">{locationLabel}</span> : null}
                          </span>
                        </button>
                      );
                    })}

                    {cellEvents.length > 2 ? (
                      <div className="event-calendar-more">+{cellEvents.length - 2} autre(s)</div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
